import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Api from '@/util/Api';
import type { ApiResponse } from '@/types/api';
import { availabilityHeaders } from './availability-api';

/**
 * A short-lived hold on one appointment slot — the React counterpart of legacy `reserveSlot` /
 * `reserveClear` in `View/js/cart-gcal.js` (:609-717) over `POST checkout/reservations` and
 * `POST checkout/reservations/cancel` (`CheckoutReservationsController`, wire shapes in
 * `CheckoutReservationModels.cs`).
 *
 * A hold only HIDES the slot from other people until it expires or is cancelled; nothing claims
 * it. So this hook owns the whole lifetime: one hold at a time, the previous one given up in the
 * same call that takes the next (`cancelReservationIds` — the route cancels first, because a hold
 * blocks its own slot), dropped when it expires, and cancelled when the host unmounts or the page
 * is left (`pagehide`, with `keepalive` so the request outlives the document).
 *
 * It knows nothing about the page using it: the reschedule page (ROBO-616) is the first host, and
 * the public cart migration and ROBO-694 are expected to be the next.
 *
 * WHO THE CALLER IS. With `downloadGuid` set the calls go out with NO session (`skipAuth`) and the
 * guid in `hd-download-guid`, which the API accepts as proof in place of a Turnstile pass for the
 * assignee of an open, still-reschedulable task on that guid's site (ROBO-616 contract A). A guest's
 * hold is capped at 15 minutes server-side; the countdown uses the lifetime the route answers (`e`). Without it they carry the
 * signed-in user's bearer token and no guid.
 *
 * ★ A GUEST'S CALLS NAME THE BRAND (`hd-brand-host`; ROBO-694 failed QA, 2026-10-09). With no
 *   session there is no token to take the brand from, and the page reaches the API through a host
 *   that is not the brand's, so without the header the route cannot tell whose cart this is and
 *   answers 401 PASS_REQUIRED before it looks at the guid. Every guest hold failed that way on
 *   test01 while the API spec (which posts to the brand's own host) and the UI spec (which mocks
 *   the route) both passed.
 */

export const RESERVATIONS_PATH = 'checkout/reservations';
export const RESERVATIONS_CANCEL_PATH = 'checkout/reservations/cancel';

/** The cart's hold lifetime; the route defaults to it too. */
export const DEFAULT_HOLD_MINUTES = 15;

export interface SlotHoldRequest {
    teamMemberId: number;
    /** `YYYY-MM-DD`. */
    date: string;
    /** Start, as minutes after midnight. */
    minute: number;
    /** Minutes on site. */
    duration: number;
    latitude: number;
    longitude: number;
    /**
     * Tasks whose own appointment must not count as busy while the slot is checked — the ones a
     * reschedule is moving. ★ The route ignores NOTHING unless told to: a reschedule host must
     * always pass these, or an appointment blocks the times that overlap its old self.
     */
    ignoreTaskIds?: readonly number[];
}

export interface SlotHold extends SlotHoldRequest {
    /** The hold id (32 hex digits) — what a save sends as `holdIds`. */
    reservationId: string;
    /** `Date.now()`-style timestamp the hold lapses at. */
    expiresAt: number;
}

export type SlotHoldResult =
    /** `openMinutes`: that day's still-free start minutes for the member, when the API sent them. */
    | { status: 'held'; hold: SlotHold; openMinutes?: number[] }
    /** Someone else got there first. Nothing is held now — the previous hold was given up too. */
    | { status: 'taken'; openMinutes?: number[] }
    | { status: 'error'; message: string };

export interface UseSlotHoldOptions {
    /** A site's default download guid — set for a guest only (see the file header). */
    downloadGuid?: string;
    /** Hold lifetime to ask for. The API caps it. */
    expiresInMinutes?: number;
    /** The hold ran out before it was used or replaced. */
    onExpired?: (hold: SlotHold) => void;
}

/** One entry per requested member. `r` is `""` when the slot was not held. */
interface HoldEntry {
    u: number;
    e?: number;
    r: string;
    t?: number[];
}

function entriesOf(response: ApiResponse<unknown>): HoldEntry[] {
    // The route answers an envelope (`{ data: [...] }`), which `Api.response` spreads: `data` is the list.
    const data = (response as { data?: unknown }).data;
    return Array.isArray(data) ? (data as HoldEntry[]) : [];
}

export function useSlotHold(options: UseSlotHoldOptions = {}) {
    const { downloadGuid, expiresInMinutes = DEFAULT_HOLD_MINUTES, onExpired } = options;
    const [hold, setHold] = useState<SlotHold | null>(null);
    const [isHolding, setIsHolding] = useState(false);

    // The live hold, readable from the unmount / pagehide handlers and from an in-flight `take`.
    const holdRef = useRef<SlotHold | null>(null);
    const isMounted = useRef(true);
    const sequence = useRef(0);
    const onExpiredRef = useRef(onExpired);
    const guidRef = useRef(downloadGuid);
    useEffect(() => {
        onExpiredRef.current = onExpired;
        guidRef.current = downloadGuid;
    });

    const remember = useCallback((next: SlotHold | null) => {
        holdRef.current = next;
        if (isMounted.current) setHold(next);
    }, []);

    const post = useCallback((path: string, body: Record<string, unknown>, keepalive = false) => {
        const guid = guidRef.current;
        return Api.post<Record<string, unknown>, unknown>(path, body, Boolean(guid), {
            keepalive,
            ...(guid ? { headers: availabilityHeaders(guid) } : {})
        });
    }, []);

    /** Fire-and-forget: used where nobody can wait for the answer (unmount, pagehide). */
    const cancelQuietly = useCallback(
        (reservationId: string) => {
            void post(RESERVATIONS_CANCEL_PATH, { reservationIds: [reservationId] }, true);
        },
        [post]
    );

    const take = useCallback(
        async (request: SlotHoldRequest): Promise<SlotHoldResult> => {
            const run = ++sequence.current;
            const previous = holdRef.current;
            setIsHolding(true);

            const response = await post(RESERVATIONS_PATH, {
                userIds: [request.teamMemberId],
                date: request.date,
                minute: request.minute,
                duration: request.duration,
                latitude: request.latitude,
                longitude: request.longitude,
                expiresInMinutes,
                ...(request.ignoreTaskIds ? { ignoreTaskIds: [...request.ignoreTaskIds] } : {}),
                ...(previous ? { cancelReservationIds: [previous.reservationId] } : {})
            });

            const isCurrent = run === sequence.current;
            if (isCurrent && isMounted.current) setIsHolding(false);

            if (!response.ok) {
                // Whether the previous hold was cancelled is unknown; it stays tracked so that
                // unmount / release still gives it up.
                return {
                    status: 'error',
                    message:
                        (response.error instanceof Error && response.error.message) ||
                        'The time could not be held'
                };
            }

            const entry = entriesOf(response).find(row => row.u === request.teamMemberId);
            const openMinutes = Array.isArray(entry?.t) ? entry.t : undefined;

            if (!entry?.r) {
                // The route cancelled the previous hold before it looked, so nothing is held now.
                if (isCurrent) remember(null);
                return { status: 'taken', openMinutes };
            }

            const taken: SlotHold = {
                ...request,
                reservationId: entry.r,
                expiresAt: Date.now() + (entry.e ?? expiresInMinutes) * 60_000
            };

            if (!isMounted.current || !isCurrent) {
                // Nobody is waiting for this hold any more (the host unmounted, or a newer pick
                // overtook it) — give it straight back rather than leave it hiding the slot.
                cancelQuietly(taken.reservationId);
                return { status: 'error', message: 'The time is no longer wanted' };
            }

            remember(taken);
            return { status: 'held', hold: taken, openMinutes };
        },
        [cancelQuietly, expiresInMinutes, post, remember]
    );

    /** Give the hold back now (the user picked something that needs no hold, or changed mode). */
    const release = useCallback(async () => {
        const current = holdRef.current;
        if (!current) return;
        remember(null);
        await post(RESERVATIONS_CANCEL_PATH, { reservationIds: [current.reservationId] });
    }, [post, remember]);

    /** Stop tracking the hold WITHOUT cancelling it — the server already did (a successful save). */
    const forget = useCallback(() => {
        remember(null);
    }, [remember]);

    // Expiry: the server drops the hold by itself, so this only clears the local record.
    useEffect(() => {
        if (!hold) return;
        const timer = setTimeout(
            () => {
                if (holdRef.current?.reservationId !== hold.reservationId) return;
                remember(null);
                onExpiredRef.current?.(hold);
            },
            Math.max(hold.expiresAt - Date.now(), 0)
        );
        return () => clearTimeout(timer);
    }, [hold, remember]);

    // Leaving: legacy cancelled on `beforeunload` (`cart-gcal.js` :488). `pagehide` is the event
    // that also fires on mobile and for the back/forward cache.
    useEffect(() => {
        isMounted.current = true;
        const giveUp = () => {
            const current = holdRef.current;
            if (!current) return;
            holdRef.current = null;
            cancelQuietly(current.reservationId);
        };
        const handlePageHide = () => {
            giveUp();
            setHold(null);
        };
        window.addEventListener('pagehide', handlePageHide);
        return () => {
            window.removeEventListener('pagehide', handlePageHide);
            isMounted.current = false;
            giveUp();
        };
    }, [cancelQuietly]);

    const holdIds = useMemo(() => (hold ? [hold.reservationId] : []), [hold]);

    return { hold, holdIds, isHolding, take, release, forget };
}
